import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback
} from 'react';

import { Fab, Tooltip, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Autocomplete, Pagination } from '@material-ui/lab';
import axios from 'axios';
import { format } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';

import ConfirmModal from 'components/Dialogs/ConfirmModal/ConfirmModal';
import ItemsPerPage from 'components/ItemsPerPage';
import PageTitleAndFilter from 'components/PageTitleAndFilter';
import OverlayFooter from 'containers/footerMobile/footer';
import Layout from 'containers/layouts/layout';
import ClearedLabelIconDisabled from 'images/icons/signals/removesAndDeletes/trashIconLightGrayRounded.svg';
import ClearedLabelIcon from 'images/icons/signals/removesAndDeletes/trashIconNavyBlueLightGrayBgRounded.svg';
import { changeFilter, clearFilter } from 'store/reducers/productsFilterSlice';
import { Container, GlobalStyle } from 'styles/general';
import Environment from 'utils/environments';
import { getFilterOptions } from 'utils/handleFilterActions';

import LabelsCard from './components/LabelsCard';
import ValitidyFilterLabels from './components/ValidityFilterLabels';

import LabelPrintWriteOffDialog from '../products/dialogs/LabelPrintWriteOffDialog';
import {
  SelectAllBox,
  SelectAllToggleButton,
  ProductFilter,
  PaginationWrapper
} from '../products/styles';

const environments = Environment(process.env.REACT_APP_ENV);

const HeaderRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
`;

const useStyles = makeStyles(() => ({
  labelsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(320px, 1fr))',
    gap: 25
  },
  '@media (max-width: 1200px)': {
    labelsGrid: {
      gridTemplateColumns: 'repeat(2, minmax(260px, 1fr))'
    }
  },
  '@media (max-width: 680px)': {
    labelsGrid: {
      gridTemplateColumns: '1fr'
    }
  }
}));

function groupLabelsByBatchId(list) {
  const groups = new Map();
  for (const item of list || []) {
    const key = item?.batchId ?? `__no_batch__:${item.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  const result = [];
  for (const [, group] of groups) {
    const base = { ...group[0] };
    const cleared = group.filter(
      (g) =>
        g?.isLabelCleared === false ||
        String(g?.labelStatus).toUpperCase() === 'CLEARED'
    ).length;
    const total = group.length;
    const notCleared = total - cleared;
    base.clearedCount = cleared;
    base.notClearedCount = notCleared;
    base.validAt = group.reduce((acc, cur) => {
      const v = cur?.validAt || cur?.expirationAt;
      if (!acc) return v;
      return new Date(v) > new Date(acc) ? v : acc;
    }, base?.validAt);
    if (!base?.image?.url) {
      const withImg = group.find((g) => g?.image?.url);
      if (withImg) base.image = withImg.image;
    }
    base.itemIds = group.map((g) => g.id).filter(Boolean);
    result.push(base);
  }
  return result;
}

function LabelsList() {
  const history = useHistory();
  const classes = useStyles();
  const inputRef = useRef(null);
  const isPhoneScreen = useMediaQuery('(max-width: 576px)');
  const [filterOpen, setFilterOpen] = useState(true);
  const [labels, setLabels] = useState([]);
  const { id: currentUserId } = useParams();
  const dispatch = useDispatch();
  const PREV_LABEL_STATUS_MODEL = { VALID: 'VALID', EXPIRED: 'EXPIRED' };
  const RAW_STATUS = history?.location?.state?.labelStatus;
  const prevLabelStatus =
    RAW_STATUS === 'ON_DATE'
      ? 'VALID'
      : PREV_LABEL_STATUS_MODEL[RAW_STATUS] || null;
  const [selectedStatuses, setSelectedStatuses] = useState(
    prevLabelStatus ? [prevLabelStatus] : []
  );
  const [validityFilterInfos, setValidityFilterInfos] = useState({
    loading: false,
    content: []
  });
  const storedProductsFilter = useSelector(
    (currState) => currState.productsFilter.filters
  );
  const shouldUseStoredFilters = useMemo(
    () => storedProductsFilter,
    [storedProductsFilter]
  );
  const [filterFields, setFilterFields] = useState([
    [
      {
        label: 'Produto',
        filterName: 'productDescription',
        filterNameSecond: 'productId',
        placeHolder: 'Selecione...',
        type: 'autoComplete',
        key: 'products',
        optionReference: 'description',
        urlPath: `${environments.catalog}/products`,
        options: [],
        size: 'medium',
        searchByTermMode: true,
        storedValue:
          (shouldUseStoredFilters &&
            storedProductsFilter.find(
              (f) => f.filterName === 'productDescription'
            )?.productDescription) ||
          storedProductsFilter.find((f) => f.filterName === 'productId')
            ?.textValue,
        marginRight: '25px'
      },
      {
        label: 'Categoria',
        filterName: 'categoryDescription',
        placeHolder: 'Selecione...',
        type: 'newTreeSelector',
        urlPath: `${environments.catalog}/categories`,
        key: 'categoryDescription',
        page: 1,
        optionReference: 'description',
        options: [],
        size: 'medium_to_plus',
        storedValue:
          shouldUseStoredFilters &&
          storedProductsFilter.find((f) => f.filterName === 'category')
            ?.textValue,
        marginRight: '25px'
      },
      {
        label: 'Responsável',
        filterName: 'responsible',
        placeHolder: 'Selecione...',
        type: 'autoComplete',
        urlPath: `${environments.auth}/users`,
        key: 'responsible',
        searchByTermMode: true,
        optionReference: 'title',
        options: [],
        size: 'medium_to_plus',
        marginRight: '25px',
        storedValue:
          shouldUseStoredFilters &&
          storedProductsFilter.find((f) => f.filterName === 'responsible')
            ?.textValue
      },
      {
        label: 'Com/Sem Baixa',
        filterName: 'isLabelCleared',
        placeHolder: 'Selecione...',
        urlPath: '',
        type: 'autoComplete',
        key: 'isLabelCleared',
        page: 1,
        optionReference: 'description',
        options: [
          { title: 'Todos', value: 'Todas' },
          { title: 'Com baixa', value: 'true' },
          { title: 'Sem baixa', value: 'false' }
        ],
        storedValue: 'Todos',
        size: 'medium_to_plus',
        marginRight: '25px'
      }
    ]
  ]);
  const [modalSettings, setModalSettings] = useState({
    confirmModalOpen: false,
    writeOffModalOpen: false,
    selectedLabel: null
  });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [productOptions] = useState([]);
  const [productDescription, setProductDescription] = useState('');
  const [filterSelectedProduct, setFilterSelectedProduct] = useState(null);
  const debounceRefs = useRef([]);
  const [callSettings, setCallSettings] = useState({
    page: 1,
    size: 50,
    totalPages: 0,
    numberOfElements: 0,
    totalElements: 0
  });

  const clearAllDebounces = () => {
    debounceRefs.current.forEach((t) => clearTimeout(t));
    debounceRefs.current = [];
  };

  const normalizeStatus = (s) => {
    const norm = String(s || '')
      .toUpperCase()
      .replace(/\s+/g, '_');
    return norm === 'ON_DATE' ? 'VALID' : norm;
  };

  const getCardKey = useCallback((l) => l?.batchId ?? l?.id, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const selected = labels.filter((l) => selectedIds.has(getCardKey(l)));
    try {
      if (selected.length > 1 && selected.every((item) => !!item?.batchId)) {
        const batchIds = Array.from(
          new Set(selected.map((i) => i.batchId).filter(Boolean))
        );
        await axios.post(
          `${environments.catalog}/printer/products/labels/writeOff`,
          { batchIds, user: currentUserId, isLabelCleared: false },
          { headers: { 'Content-Type': 'application/json' } }
        );
      } else {
        for (const item of selected) {
          const isBatch = !!item?.batchId && Number(item?.notClearedCount) > 0;
          if (isBatch) {
            await axios.put(
              `${environments.catalog}/printer/products/labels/${item.batchId}`,
              { isLabelCleared: false, quantity: 1 },
              { headers: { 'Content-Type': 'application/json' } }
            );
          } else if (item?.id) {
            await axios.put(
              `${environments.catalog}/printer/products/labels/${item.id}`,
              { isLabelCleared: false, quantity: 1 },
              { headers: { 'Content-Type': 'application/json' } }
            );
          }
        }
      }
      setModalSettings({
        confirmModalOpen: false,
        writeOffModalOpen: false,
        selectedLabel: null
      });
      setSelectedIds(new Set());
      fetchLabels();
    } catch (error) {}
  }, [selectedIds, labels, getCardKey, environments.catalog, currentUserId]);

  const fetchLabels = async (
    searchParams = new URLSearchParams({
      restaurantId: currentUserId,
      originId: currentUserId
    }),
    page = callSettings.page,
    size = callSettings.size
  ) => {
    try {
      if (selectedStatuses?.length) {
        searchParams.set('labelStatus', selectedStatuses.join(','));
      }
      searchParams.set('page', String(page));
      searchParams.set('limit', String(size));
      searchParams.set('size', String(size));

      const response = await axios.get(
        `${environments.catalog}/printer/products/labels/list`,
        { params: Object.fromEntries(searchParams.entries()) }
      );

      const apiLabels = response.data?.labels || response.data?.content || [];
      const pagination = response.data?.pagination || {};
      const expired = Number(response?.data?.counters?.expired ?? 0);
      const toExpire = Number(response?.data?.counters?.toExpire ?? 0);
      const counters = [
        { labelStatus: 'EXPIRED', total: expired },
        { labelStatus: 'VALID', total: toExpire }
      ];
      setValidityFilterInfos({ loading: false, content: counters });

      const filteredForView = selectedStatuses?.length
        ? apiLabels.filter((l) =>
            selectedStatuses.includes(normalizeStatus(l?.labelStatus))
          )
        : apiLabels;

      const grouped = groupLabelsByBatchId(filteredForView);
      setLabels(grouped);
      setSelectedIds(new Set());

      setCallSettings((prev) => ({
        ...prev,
        page: Number(pagination.page ?? page),
        size: Number(pagination.limit ?? size),
        totalPages: Number(pagination.totalPages ?? prev.totalPages ?? 0),
        numberOfElements: grouped.length,
        totalElements: Number(
          pagination.total ??
            response?.data?.totalElements ??
            response?.data?.counters?.total ??
            prev.totalElements ??
            grouped.length
        )
      }));
    } catch (error) {}
  };

  useEffect(() => {
    fetchLabels();
  }, [currentUserId, selectedStatuses]);

  useEffect(() => {
    return () => clearAllDebounces();
  }, []);

  const normalizeForModal = (item) => {
    const clearedCount = Number(item?.clearedCount ?? 0);
    const notClearedCount = Number(item?.notClearedCount ?? 0);
    const itemIds = Array.isArray(item?.itemIds) ? item.itemIds : [];
    return {
      ...item,
      clearedCount: Number.isFinite(clearedCount) ? clearedCount : 0,
      notClearedCount: Number.isFinite(notClearedCount) ? notClearedCount : 0,
      itemIds
    };
  };

  const clearLabel = async (labelItem, quantity = 1) => {
    const isBatch =
      !!labelItem?.batchId && Number(labelItem?.notClearedCount) > 1;
    try {
      if (isBatch) {
        const targetId = labelItem.batchId;
        await axios.put(
          `${environments.catalog}/printer/products/labels/${targetId}`,
          { isLabelCleared: false, quantity: Number(quantity) },
          { headers: { 'Content-Type': 'application/json' } }
        );
      } else {
        const labelId = labelItem?.id;
        await axios.put(
          `${environments.catalog}/printer/products/labels/${labelId}`,
          { isLabelCleared: false, quantity: Number(quantity) }
        );
      }
      setModalSettings({
        confirmModalOpen: false,
        writeOffModalOpen: false,
        selectedLabel: null
      });
      fetchLabels();
    } catch (error) {}
  };

  const handleClearLabelClick = (labelItem) => {
    const cleared = Number(labelItem?.clearedCount ?? 0);
    const notCleared = Number(labelItem?.notClearedCount ?? 0);
    const showGroupedCard = cleared + notCleared > 1;
    if (showGroupedCard) {
      setModalSettings({
        writeOffModalOpen: true,
        confirmModalOpen: false,
        selectedLabel: normalizeForModal(labelItem)
      });
    } else {
      const validAt = labelItem?.validAt || labelItem?.expirationAt;
      setModalSettings({
        writeOffModalOpen: false,
        confirmModalOpen: true,
        selectedLabel: {
          ...normalizeForModal(labelItem),
          modalTitle: format(new Date(validAt), 'dd/MM/yyyy - HH:mm'),
          modalContent: 'Deseja realmente efetuar a baixa desta etiqueta?',
          onConfirm: () => clearLabel(labelItem, 1),
          onClose: () =>
            setModalSettings((prev) => ({
              ...prev,
              confirmModalOpen: false,
              selectedLabel: null
            }))
        }
      });
    }
  };

  const handleFilter = useCallback(
    (param, value, filter, paramSecond) => {
      let updatedFilters = [...(shouldUseStoredFilters || [])];
      const isAutoCompleteObject = typeof value === 'object' && value !== null;
      if (!value) {
        dispatch(clearFilter());
        updatedFilters = updatedFilters.filter(
          (el) => el?.param !== param && el?.param !== paramSecond
        );
        setFilterFields((prev) =>
          prev.map((row) =>
            row.map((f) =>
              f.filterName === param || f.filterNameSecond === paramSecond
                ? { ...f, storedValue: '' }
                : f
            )
          )
        );
        if (param === 'labelStatus') setSelectedStatuses([]);
        const baseParams = new URLSearchParams({
          restaurantId: currentUserId,
          originId: currentUserId
        });
        fetchLabels(baseParams, 1, callSettings.size);
        setCallSettings((prev) => ({ ...prev, page: 1 }));
        return;
      }
      if (param === 'labelStatus') {
        const values = String(value)
          .split(',')
          .map((v) => normalizeStatus(v));
        setSelectedStatuses(values);
        const entry = {
          param: 'labelStatus',
          value: values.join(','),
          textValue: values.join(','),
          urlParam: `labelStatus=${encodeURIComponent(values.join(','))}`
        };
        updatedFilters = updatedFilters.filter(
          (el) => el.param !== 'labelStatus'
        );
        updatedFilters.push(entry);
        setCallSettings((prev) => ({ ...prev, page: 1 }));
        return;
      }
      if (param === 'productDescription' && paramSecond === 'productId') {
        let entry;
        if (isAutoCompleteObject) {
          entry = {
            param: 'productId',
            value: value.id,
            textValue: value.description,
            urlParam: `productId=${encodeURIComponent(value.id)}`
          };
        } else {
          entry = {
            param: 'productDescription',
            value: value,
            textValue: value,
            urlParam: `productDescription=${encodeURIComponent(value)}`
          };
        }
        updatedFilters = updatedFilters.filter(
          (el) => el.param !== 'productId' && el.param !== 'productDescription'
        );
        updatedFilters.push(entry);
        dispatch(changeFilter(entry));
        setFilterFields((prev) =>
          prev.map((row) =>
            row.map((f) =>
              f.filterName === 'productDescription' ||
              f.filterNameSecond === 'productId'
                ? { ...f, storedValue: value?.description || value }
                : f
            )
          )
        );
      } else if (param === 'responsible') {
        const id = isAutoCompleteObject
          ? value.id ?? value.value ?? value._id
          : value;
        const text = isAutoCompleteObject
          ? value.title ?? value.label ?? String(id ?? '')
          : String(value ?? '');
        const entry = {
          param,
          value: id,
          textValue: text,
          urlParam: `${param}=${encodeURIComponent(id ?? '')}`
        };
        updatedFilters = updatedFilters.filter((el) => el.param !== param);
        updatedFilters.push(entry);
        dispatch(changeFilter(entry));
        setFilterFields((prev) =>
          prev.map((row) =>
            row.map((f) =>
              f.filterName === param ? { ...f, storedValue: text } : f
            )
          )
        );
      } else {
        const entry = {
          param,
          value,
          textValue: filter?.title || value,
          urlParam: `${param}=${encodeURIComponent(value)}`
        };
        updatedFilters = updatedFilters.filter((el) => el.param !== param);
        updatedFilters.push(entry);
        dispatch(changeFilter(entry));
        setFilterFields((prev) =>
          prev.map((row) =>
            row.map((f) =>
              f.filterName === param ? { ...f, storedValue: value } : f
            )
          )
        );
      }
      const searchParams = new URLSearchParams({
        restaurantId: currentUserId,
        originId: currentUserId
      });
      updatedFilters.forEach((f) => {
        if (f.urlParam) {
          const [k, v] = f.urlParam.split('=');
          searchParams.set(k, decodeURIComponent(v));
        }
      });
      setCallSettings((prev) => ({ ...prev, page: 1 }));
      fetchLabels(searchParams, 1, callSettings.size);
    },
    [currentUserId, shouldUseStoredFilters, dispatch, callSettings.size]
  );

  const getFilterhandle = async (urlPath, label, key, optionReference) => {
    if (key === 'responsible') {
      try {
        const response = await axios.get(
          `${urlPath}?originId=${currentUserId}&page=1&size=100`
        );
        const users = response.data?.content || [];
        const options = users.map((user) => ({
          id: user._id,
          title: user._id,
          value: user._id,
          label: user._id
        }));
        setFilterFields((prev) =>
          prev.map((row) =>
            row.map((f) => (f.key === 'responsible' ? { ...f, options } : f))
          )
        );
      } catch (error) {}
      return;
    }
    getFilterOptions(
      urlPath,
      label,
      key,
      filterFields,
      setFilterFields,
      optionReference,
      currentUserId,
      '',
      1
    );
  };

  const allSelected = useMemo(
    () => labels.length > 0 && selectedIds.size === labels.length,
    [labels.length, selectedIds.size]
  );

  const toggleSelectAll = useCallback(() => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(labels.map((l) => getCardKey(l))));
  }, [allSelected, labels, getCardKey]);

  const handleClearProductFilter = () => {
    setProductDescription('');
    setFilterSelectedProduct(null);
  };

  const openBulkConfirm = () => {
    if (selectedIds.size === 0) return;
    const qty = selectedIds.size;
    setModalSettings({
      confirmModalOpen: true,
      writeOffModalOpen: false,
      selectedLabel: {
        modalTitle: 'Confirmação',
        modalContent: `Deseja realmente efetuar a baixa de ${qty} ${
          qty === 1 ? 'etiqueta' : 'etiquetas'
        }?`,
        onConfirm: () => clearSelected(),
        onClose: () =>
          setModalSettings((prev) => ({
            ...prev,
            confirmModalOpen: false,
            selectedLabel: null
          }))
      }
    });
  };

  return (
    <Layout>
      <GlobalStyle />
      <Container>
        <PageTitleAndFilter
          title="Etiquetas"
          activeFilterFilds={filterFields}
          handleFilter={handleFilter}
          additionalButtonMobile={null}
          complementarybutton={null}
          setFilterOpenedMainPageControl={setFilterOpen}
          getFilterhandle={getFilterhandle}
          dynamicJustifyOff
        />
        <ValitidyFilterLabels
          initialActive={prevLabelStatus ? [prevLabelStatus] : []}
          validityFilterInfos={validityFilterInfos}
          onClick={(selectedKeys) => {
            const normalized = (selectedKeys || []).map((k) =>
              String(k).toUpperCase()
            );
            setSelectedStatuses(normalized);
            handleFilter('labelStatus', normalized.join(','));
          }}
          filterOpen={filterOpen}
        />
        {isPhoneScreen ? (
          <ProductFilter>
            <HeaderRow>
              <Autocomplete
                freeSolo
                className="autocompleteSmall productFilterAutocomplete"
                fullWidth
                size="small"
                variant="outlined"
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option?.description
                }
                options={productOptions}
                value={filterSelectedProduct || null}
                inputValue={productDescription || ''}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    setProductDescription(value);
                    clearAllDebounces();
                    const t = setTimeout(() => {
                      handleFilter(
                        'productDescription',
                        value,
                        { title: value },
                        'productId'
                      );
                    }, 600);
                    debounceRefs.current.push(t);
                  }
                  if (reason === 'clear') {
                    handleClearProductFilter();
                    handleFilter('productDescription', null, null, 'productId');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id={params.id}
                    name="product"
                    placeholder="Localize o produto para imprimir..."
                  />
                )}
              />
              <SelectAllToggleButton
                onClick={toggleSelectAll}
                $active={allSelected}
                disabled={labels.length === 0}
                aria-pressed={allSelected}
              >
                {allSelected ? 'LIMPAR SELEÇÃO' : 'SELECIONAR TODAS'}
              </SelectAllToggleButton>
            </HeaderRow>
          </ProductFilter>
        ) : (
          <SelectAllBox filterOpen={filterOpen} simplifiedVersion={false}>
            <SelectAllToggleButton
              onClick={toggleSelectAll}
              $active={allSelected}
              disabled={labels.length === 0}
              aria-pressed={allSelected}
            >
              {allSelected ? 'LIMPAR SELEÇÃO' : 'SELECIONAR TODAS'}
            </SelectAllToggleButton>
          </SelectAllBox>
        )}
        <div className={classes.labelsGrid}>
          {labels.map((label) => {
            const key = getCardKey(label);
            return (
              <LabelsCard
                key={key}
                labelItem={label}
                onClear={handleClearLabelClick}
                selected={selectedIds.has(key)}
                onToggleSelected={() => toggleSelect(key)}
              />
            );
          })}
        </div>

        <PaginationWrapper className="pagination">
          <Pagination
            count={callSettings.totalPages}
            page={callSettings.page}
            onChange={(event, nextPage) => {
              if (event) {
                setCallSettings((prev) => ({ ...prev, page: nextPage }));
                const baseParams = new URLSearchParams({
                  restaurantId: currentUserId,
                  originId: currentUserId
                });
                if (selectedStatuses?.length) {
                  baseParams.set('labelStatus', selectedStatuses.join(','));
                }
                fetchLabels(baseParams, nextPage, callSettings.size);
              }
            }}
            variant="outlined"
            shape="rounded"
            size="small"
            color="primary"
          />

          <ItemsPerPage
            numberOfElements={callSettings.numberOfElements}
            size={callSettings.totalElements}
          />
        </PaginationWrapper>

        <ConfirmModal
          open={modalSettings.confirmModalOpen}
          title={modalSettings.selectedLabel?.modalTitle || 'Confirmação'}
          content={
            <span className="bold">
              {modalSettings.selectedLabel?.modalContent ||
                'Confirmar operação?'}
            </span>
          }
          confirmText="Sim"
          cancelText="Não"
          onConfirm={modalSettings.selectedLabel?.onConfirm}
          onClose={modalSettings.selectedLabel?.onClose}
        />
        <LabelPrintWriteOffDialog
          open={modalSettings.writeOffModalOpen}
          onClose={() =>
            setModalSettings((prev) => ({
              ...prev,
              writeOffModalOpen: false,
              selectedLabel: null
            }))
          }
          onSubmit={(label, numberOfLabels) =>
            clearLabel(label, numberOfLabels)
          }
          labelItem={modalSettings.selectedLabel}
        />
        <Tooltip
          className="floatButtonLabel"
          title={
            selectedIds.size === 0
              ? 'Selecione ao menos 1 etiqueta'
              : 'Efetuar baixa'
          }
        >
          <Fab
            color="primary"
            aria-label="baixa"
            size="medium"
            style={{ outline: 0 }}
            disabled={selectedIds.size === 0}
            onClick={openBulkConfirm}
          >
            <img
              src={
                selectedIds.size === 0
                  ? ClearedLabelIconDisabled
                  : ClearedLabelIcon
              }
              alt="Efetuar baixa"
            />
          </Fab>
        </Tooltip>
      </Container>
      <OverlayFooter />
    </Layout>
  );
}

export default LabelsList;
